import { useEffect, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MOTIVO_IGNORAR_MINIMO } from "@/data/folha";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  titulo: string;
  descricao: ReactNode;
  /** Lança erro para manter o modal aberto (o toast fica por conta daqui). */
  onConfirmar: (motivo: string) => Promise<void>;
}

/**
 * Todo "Ignorar" da folha passa por aqui: sem motivo de pelo menos
 * MOTIVO_IGNORAR_MINIMO caracteres não dá para confirmar.
 */
export const IgnorarMotivoDialog = ({ open, onClose, titulo, descricao, onConfirmar }: Props) => {
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) setMotivo("");
  }, [open]);

  const tamanho = motivo.trim().length;
  const valido = tamanho >= MOTIVO_IGNORAR_MINIMO;

  const confirmar = async () => {
    if (!valido) return;
    setSalvando(true);
    try {
      await onConfirmar(motivo.trim());
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao ignorar");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !salvando && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">{descricao}</div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="motivo-ignorar">Motivo</Label>
          <Textarea
            id="motivo-ignorar"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Ex: Colaborador desligado em setembro"
            autoFocus
          />
          <p className={`text-xs ${valido ? "text-muted-foreground" : "text-amber-700 dark:text-amber-400"}`}>
            {valido
              ? `${tamanho} caracteres`
              : `Mínimo de ${MOTIVO_IGNORAR_MINIMO} caracteres (${tamanho}/${MOTIVO_IGNORAR_MINIMO})`}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button onClick={() => void confirmar()} disabled={!valido || salvando}>
            {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Ignorar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
